import type {
  GroupedSignal,
  SignalListGroupByField,
  SignalListGroupEndpoint,
} from '@terra/shared-contracts-signals';
import {
  FindingSeveritySchema,
  type FindingSeverityType,
} from '@terra/shared-schemas-orchestrator';
import type {
  SignalsListGrouping,
  SignalsTabFilterState,
} from '../../../store/tab/signalsTabScopedFilterStore';

export type SignalTrace = NonNullable<GroupedSignal['traces']>[number];
export type SignalsSortField = SignalsTabFilterState['sortField'];
export type SignalsSortOrder = SignalsTabFilterState['sortOrder'];
export type SignalsSort = { field: SignalsSortField; order: SignalsSortOrder };

export const DEFAULT_SIGNALS_SORT: SignalsSort = {
  field: 'createdAt',
  order: 'desc',
};

const SIGNALS_SORT_FIELD_BY_COLUMN_ID = {
  name: 'description',
  domain: 'domain',
  relatedEndpoint: 'endpoint',
  appWide: 'appWide',
  status: 'status',
  stage: 'stage',
  source: 'source',
  category: 'category',
  relatedExecution: 'execution',
  relatedFinding: 'finding',
  createdAt: 'createdAt',
} as const satisfies Record<string, SignalsSortField>;

export const SIGNALS_GROUP_COLUMN_ID_BY_FIELD = {
  status: 'status',
  stage: 'stage',
  category: 'category',
  source: 'source',
  endpoint: 'relatedEndpoint',
  execution: 'relatedExecution',
  finding: 'relatedFinding',
  asset: null,
} as const satisfies Record<SignalListGroupByField, string | null>;

export const toSignalsOrderBy = ({
  sort,
  grouping,
}: {
  sort: SignalsSort;
  grouping: SignalsListGrouping;
}): SignalsSort[] => {
  if (!grouping) {
    return [sort];
  }

  return [
    { field: grouping.field, order: grouping.order },
    sort.field === grouping.field ? DEFAULT_SIGNALS_SORT : sort,
  ];
};

type SignalsSortableColumnId = keyof typeof SIGNALS_SORT_FIELD_BY_COLUMN_ID;

const isSignalsSortableColumnId = (
  columnId: string,
): columnId is SignalsSortableColumnId =>
  Object.hasOwn(SIGNALS_SORT_FIELD_BY_COLUMN_ID, columnId);

export const isSignalsSortableColumn = (columnId: string): boolean =>
  isSignalsSortableColumnId(columnId);

export const toSignalsSortField = (
  columnId: string,
): SignalsSortField | null =>
  isSignalsSortableColumnId(columnId)
    ? SIGNALS_SORT_FIELD_BY_COLUMN_ID[columnId]
    : null;

export const toSignalsSortColumnId = (
  field: SignalsSortField,
): SignalsSortableColumnId | null =>
  Object.keys(SIGNALS_SORT_FIELD_BY_COLUMN_ID)
    .filter(isSignalsSortableColumnId)
    .find((columnId) => SIGNALS_SORT_FIELD_BY_COLUMN_ID[columnId] === field) ??
  null;

export const SIGNAL_DETAIL_TAB = {
  ANALYSIS: 'analysis',
  CONFIDENCE_REASON: 'confidenceReason',
  SECURITY_TEST_CASE: 'securityTestCase',
  EXECUTION_SUMMARY: 'executionSummary',
} as const;

export type SignalDetailTab =
  (typeof SIGNAL_DETAIL_TAB)[keyof typeof SIGNAL_DETAIL_TAB];

const SIGNAL_DETAIL_TAB_CONTENT: Record<
  SignalDetailTab,
  (signal: GroupedSignal) => string | null | undefined
> = {
  [SIGNAL_DETAIL_TAB.ANALYSIS]: (signal) => signal.analysis,
  [SIGNAL_DETAIL_TAB.CONFIDENCE_REASON]: (signal) => signal.reason,
  [SIGNAL_DETAIL_TAB.SECURITY_TEST_CASE]: (signal) =>
    signal.securityTestCaseSummary,
  [SIGNAL_DETAIL_TAB.EXECUTION_SUMMARY]: (signal) => signal.smokeTestSummary,
};

export const getSignalDetailTabContent = ({
  signal,
  tab,
}: {
  signal: GroupedSignal;
  tab: SignalDetailTab;
}): string => SIGNAL_DETAIL_TAB_CONTENT[tab](signal)?.trim() ?? '';

export const resolveSignalDetailTabs = (
  signal: GroupedSignal,
): SignalDetailTab[] =>
  Object.values(SIGNAL_DETAIL_TAB).filter(
    (tab) => getSignalDetailTabContent({ signal, tab }) !== '',
  );

const parseUrl = (url: string): URL | null => {
  try {
    return new URL(url);
  } catch {
    return null;
  }
};

export const resolveSignalDomain = (signal: GroupedSignal): string | null => {
  const endpointUrl = signal.endpoint?.url;

  if (endpointUrl) {
    return parseUrl(endpointUrl)?.hostname || endpointUrl;
  }

  return signal.targetSummary?.trim() || null;
};

export type SignalEndpointSummary = {
  method: string;
  path: string;
};

export const decodeDisplayPath = (path: string): string => {
  try {
    return decodeURI(path);
  } catch {
    return path;
  }
};

const toSignalEndpointPath = ({
  url,
  graphqlOperationName,
}: {
  url: string;
  graphqlOperationName?: string | null;
}): string => {
  const parsedUrl = parseUrl(url);
  const basePath = decodeDisplayPath(
    parsedUrl ? `${parsedUrl.pathname}${parsedUrl.search}` : url,
  );

  return graphqlOperationName
    ? `${basePath}?${graphqlOperationName}`
    : basePath;
};

export const resolveSignalEndpoint = (
  signal: GroupedSignal,
): SignalEndpointSummary | null => {
  if (!signal.endpoint || signal.isAppWide === true) {
    return null;
  }

  return {
    method: signal.endpoint.request.method,
    path: toSignalEndpointPath(signal.endpoint),
  };
};

export const formatSignalEndpointGroupLabel = (
  endpoint: SignalListGroupEndpoint,
): string =>
  [endpoint.method, toSignalEndpointPath(endpoint)].filter(Boolean).join(' ');

const NEO_TRACE_NAME = 'neo';

export const sortSignalNeoTracesNewestFirst = (
  traces: GroupedSignal['traces'],
): SignalTrace[] =>
  (traces ?? [])
    .filter((trace) => trace.name === NEO_TRACE_NAME)
    .sort(
      (left, right) =>
        new Date(right.createdAt).getTime() -
        new Date(left.createdAt).getTime(),
    );

export const resolveLatestSignalTrace = (
  signal: GroupedSignal,
): SignalTrace | null =>
  sortSignalNeoTracesNewestFirst(signal.traces)[0] ?? null;

export const resolveSignalLastTestedAt = (
  signal: GroupedSignal,
): Date | null => {
  const latestNeoTrace = resolveLatestSignalTrace(signal);
  const testedDates = [
    signal.lastSmokeTestedAt ? new Date(signal.lastSmokeTestedAt) : null,
    latestNeoTrace ? new Date(latestNeoTrace.createdAt) : null,
  ].filter((date): date is Date => date !== null);

  return testedDates.reduce<Date | null>(
    (latest, date) => (!latest || date > latest ? date : latest),
    null,
  );
};

export const toFindingSeverity = (
  severity: string | null | undefined,
): FindingSeverityType | null => {
  const parsed = FindingSeveritySchema.safeParse(severity);

  return parsed.success ? parsed.data : null;
};

export const formatSignalConfidenceLabel = (
  level: string | null,
): string | null =>
  level ? `${level.charAt(0)}${level.slice(1).toLowerCase()}` : null;

export const SIGNALS_GRID_ROW_KIND = {
  SIGNAL: 'signal',
  DETAIL: 'detail',
} as const;

export type SignalsGridRow =
  | {
      rowKind: typeof SIGNALS_GRID_ROW_KIND.SIGNAL;
      id: string;
      signal: GroupedSignal;
    }
  | {
      rowKind: typeof SIGNALS_GRID_ROW_KIND.DETAIL;
      id: string;
      signal: GroupedSignal;
    };

export const toSignalDetailRowId = (signalId: string) => `${signalId}:detail`;

const hasSignalsRowKind = ({
  row,
  rowKind,
}: {
  row: object | null | undefined;
  rowKind: SignalsGridRow['rowKind'];
}): boolean => row != null && Reflect.get(row, 'rowKind') === rowKind;

export const isSignalDetailRow = (
  row: object | null | undefined,
): row is Extract<
  SignalsGridRow,
  { rowKind: typeof SIGNALS_GRID_ROW_KIND.DETAIL }
> => hasSignalsRowKind({ row, rowKind: SIGNALS_GRID_ROW_KIND.DETAIL });

export const isSignalDataRow = (
  row: object | null | undefined,
): row is Extract<
  SignalsGridRow,
  { rowKind: typeof SIGNALS_GRID_ROW_KIND.SIGNAL }
> => hasSignalsRowKind({ row, rowKind: SIGNALS_GRID_ROW_KIND.SIGNAL });

export const buildSignalsGridRows = ({
  signals,
  expandedSignalIds,
}: {
  signals: readonly GroupedSignal[];
  expandedSignalIds: ReadonlySet<string>;
}): SignalsGridRow[] =>
  signals.flatMap((signal): SignalsGridRow[] => {
    const signalRow: SignalsGridRow = {
      rowKind: SIGNALS_GRID_ROW_KIND.SIGNAL,
      id: signal.id,
      signal,
    };

    return expandedSignalIds.has(signal.id)
      ? [
          signalRow,
          {
            rowKind: SIGNALS_GRID_ROW_KIND.DETAIL,
            id: toSignalDetailRowId(signal.id),
            signal,
          },
        ]
      : [signalRow];
  });
